import {
  findAuthoritativeProjectParameterBindingKind,
  type AvailableProjectParameterDescriptor,
  type ParameterBindingKind,
  type ParameterTypeHint,
} from '../../revit/projectParameterMutationPlan';
import { sanitizeRevitParameterNames } from '../../revitParameterNames';
import { groupParameterEntriesByBindingKind } from './parameterBindingGrouping';

const ALWAYS_PROJECT_INSTANCE_PARAMETER_NAMES = new Set([
  '#pcf_iso_messsage',
  '#pcf_rd_undimensioned',
]);

export interface MappedProjectParameterBindingGroup {
  parameterNames: string[];
  bindingKind: ParameterBindingKind;
}

export function requiresMappedProjectInstanceBinding(parameterName: string): boolean {
  return ALWAYS_PROJECT_INSTANCE_PARAMETER_NAMES.has(parameterName.trim().toLowerCase());
}

export function buildMappedProjectParameterBindingGroups(input: {
  parameterNames: string[];
  preferredBindingKind: ParameterBindingKind;
}): MappedProjectParameterBindingGroup[] {
  return groupParameterEntriesByBindingKind(
    sanitizeRevitParameterNames(input.parameterNames).map((parameterName) => ({
      parameterName,
      bindingKind: requiresMappedProjectInstanceBinding(parameterName)
        ? 'instance'
        : input.preferredBindingKind,
    }))
  );
}

export function adoptExistingMappedProjectParameterBindings(
  groups: MappedProjectParameterBindingGroup[],
  availableParameters: AvailableProjectParameterDescriptor[],
  parameterTypeHints: ParameterTypeHint[],
): MappedProjectParameterBindingGroup[] {
  return groupParameterEntriesByBindingKind(
    groups.flatMap((group) =>
      group.parameterNames.map((parameterName) => ({
        parameterName,
        bindingKind: requiresMappedProjectInstanceBinding(parameterName)
          ? 'instance'
          : (findAuthoritativeProjectParameterBindingKind(
              parameterName,
              availableParameters,
              parameterTypeHints,
            ) ?? group.bindingKind),
      }))
    )
  );
}

export function enforceMappedProjectParameterBindingGroups(
  groups: MappedProjectParameterBindingGroup[],
): MappedProjectParameterBindingGroup[] {
  return groupParameterEntriesByBindingKind(
    groups.flatMap((group) =>
      sanitizeRevitParameterNames(group.parameterNames).map((parameterName) => ({
        parameterName,
        bindingKind: requiresMappedProjectInstanceBinding(parameterName)
          ? 'instance'
          : group.bindingKind,
      }))
    )
  );
}
